"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import {
  X, Check, CheckCircle2, Phone, Clock, Calendar, Bell,
  Trash2, AlertCircle, User, ListTodo, GraduationCap,
} from "@/lib/icons"
import { getTasks, completeTask, deleteTask } from "@/lib/supabase"
import { useEscapeKey } from "@/hooks/useEscapeKey"

const TIPO_CONFIG = {
  manual: { label: "Tarea", color: "#64748b", bg: "bg-slate-100 dark:bg-slate-800" },
  seguimiento: { label: "Seguimiento", color: "#0ea5e9", bg: "bg-sky-50 dark:bg-sky-950/20" },
  recordatorio: { label: "Recordatorio", color: "#f59e0b", bg: "bg-amber-50 dark:bg-amber-950/20" },
  llamada: { label: "Llamada", color: "#8b5cf6", bg: "bg-violet-50 dark:bg-violet-950/20" },
  cita: { label: "Cita", color: "#10b981", bg: "bg-emerald-50 dark:bg-emerald-950/20" },
}

const TIPO_ICONS = {
  manual: ListTodo,
  seguimiento: Bell,
  recordatorio: Clock,
  llamada: Phone,
  cita: Calendar,
}

export function TasksPanel({ open, onClose, user, asesorId, onNavigateToLead }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState("pendientes") // pendientes | completadas

  useEscapeKey(open, onClose)

  const loadTasks = async () => {
    setLoading(true)
    const { data } = await getTasks({
      asesorId: asesorId || null,
      includeCompleted: filter === "completadas",
    })
    if (filter === "completadas") {
      setTasks(data.filter(t => t.completada))
    } else {
      setTasks(data.filter(t => !t.completada))
    }
    setLoading(false)
  }

  useEffect(() => {
    if (open) loadTasks()
  }, [open, filter, asesorId])

  const handleComplete = async (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId))
    const { error } = await completeTask(taskId)
    if (error) {
      toast.error("Error al completar")
      loadTasks()
    } else {
      toast.success("Tarea completada")
    }
  }

  const handleDelete = async (taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId))
    const { error } = await deleteTask(taskId)
    if (error) {
      toast.error("Error al eliminar")
      loadTasks()
    }
  }

  const pendingCount = tasks.length

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex justify-end bg-black/40 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 border-l border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-slate-200 dark:border-slate-700" style={{ background: "linear-gradient(135deg, #7a1b33 0%, #5a1523 100%)" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
                <ListTodo className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white">Mis Tareas</h2>
                <p className="text-[11px] text-white/70">{pendingCount} pendiente{pendingCount !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/15 text-white/70 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Filtros */}
          <div className="flex gap-1.5 mt-3">
            {[
              { key: "pendientes", label: "Pendientes" },
              { key: "completadas", label: "Completadas" },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${filter === f.key ? "bg-white text-slate-900 shadow-sm" : "text-white/70 hover:text-white hover:bg-white/10"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Aviso: las tareas se crean desde el prospecto */}
        <div className="flex-shrink-0 px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
          <div className="rounded-lg bg-rose-50/60 dark:bg-rose-950/20 border border-rose-200/50 dark:border-rose-900/30 px-3 py-2">
            <p className="text-[10.5px] text-rose-700 dark:text-rose-300 leading-snug">
              💡 Las tareas nuevas se crean dentro de cada prospecto, así quedan ligadas. Haz click en una tarea para ir directo a su prospecto.
            </p>
          </div>
        </div>

        {/* Lista de tareas */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-rose-300 border-t-rose-600" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-8 w-8 text-slate-300 dark:text-slate-600" />
              </div>
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {filter === "completadas" ? "Sin tareas completadas" : "Todo al dia"}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                {filter === "completadas" ? "Las tareas completadas aparecen aqui" : "Crea una tarea para empezar"}
              </p>
            </div>
          ) : (
            tasks.map(task => {
              const tipoConf = TIPO_CONFIG[task.tipo] || TIPO_CONFIG.manual
              const TipoIcon = TIPO_ICONS[task.tipo] || ListTodo
              const isHigh = task.prioridad === "alta"

              const hasLead = Boolean(task.lead?.id || task.lead_id)
              const handleTaskClick = (e) => {
                // Evita que el click en checkbox/delete dispare navegación
                if (e.target.closest("button")) return
                if (hasLead && onNavigateToLead) {
                  onNavigateToLead(task.lead || { id: task.lead_id })
                }
              }
              return (
                <div
                  key={task.id}
                  onClick={hasLead ? handleTaskClick : undefined}
                  className={`group relative rounded-xl border p-3 transition-all hover:shadow-md ${
                    hasLead ? "cursor-pointer" : ""
                  } ${
                    task.completada
                      ? "bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 opacity-60"
                      : isHigh
                        ? "bg-white dark:bg-slate-900 border-red-200 dark:border-red-900/40 shadow-sm shadow-red-100/50"
                        : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    {!task.completada && (
                      <button
                        onClick={() => handleComplete(task.id)}
                        className="mt-0.5 h-5 w-5 rounded-md border-2 border-slate-300 dark:border-slate-600 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-all flex-shrink-0 flex items-center justify-center group/check"
                      >
                        <Check className="h-3 w-3 text-transparent group-hover/check:text-emerald-500 transition-colors" />
                      </button>
                    )}
                    {task.completada && (
                      <div className="mt-0.5 h-5 w-5 rounded-md bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium leading-tight ${task.completada ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"}`}>
                        {task.titulo}
                      </p>

                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {/* Tipo badge */}
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${tipoConf.bg}`}
                          style={{ color: tipoConf.color }}
                        >
                          <TipoIcon className="h-2.5 w-2.5" />
                          {tipoConf.label}
                        </span>

                        {/* Prioridad */}
                        {isHigh && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-red-100 dark:bg-red-950/30 text-red-600 dark:text-red-400">
                            <AlertCircle className="h-2.5 w-2.5" /> Urgente
                          </span>
                        )}

                        {/* Lead asociado */}
                        {task.lead?.nombre_tutor && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400">
                            <User className="h-2.5 w-2.5" />
                            {task.lead.nombre_tutor}
                          </span>
                        )}

                        {/* Fecha */}
                        <span className="text-[10px] text-slate-400">
                          {new Date(task.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
                        </span>
                      </div>
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="p-1 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
